import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@chakra-ui/react";
import DropFilesHere from "modules/messaging/_shared/drop-files-here";
import { usePanelSize } from "modules/providers/panel-size-provider";
import React, { memo, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { HAPPYCO_THREAD_DRAFT_ID } from "modules/utils/Constants";
import { useFile } from "modules/providers/file-provider";
import { MessageThreadType } from "gql/graphql";
import QuillMessageComposer, {
  IQuillEditorHandle,
} from "modules/messaging/compose/quill-message-composer";
import ComposeMessageThreadHeader from "modules/messaging/_shared/compose-message-thread-header";
import HappycoComposeHeader from "./happyco-compose-header";
import useHappycoWorkOrder from "./use-happyco-work-order";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";

const ComposeHappycoThreadDrawer: React.FC = () => {
  const { draft, setSubject } = useMessageThreadDraft({
    draftId: HAPPYCO_THREAD_DRAFT_ID,
  });
  const { uploadFiles } = useFile();
  const { rightWidth } = usePanelSize();
  const { happycoWorkOrder } = useHappycoWorkOrder({
    happycoWorkOrderId: draft?.happycoWorkOrderId,
  });
  const quillEditorRef = useRef<IQuillEditorHandle>(null);

  useEffect(() => {
    if (!happycoWorkOrder) return;
    setSubject(
      `${happycoWorkOrder.property} • ${happycoWorkOrder.location} | ${happycoWorkOrder.category}`.substring(
        0,
        60,
      ),
    );
  }, [happycoWorkOrder, setSubject]);

  const onFileListUpload = useCallback(
    (files: File[]) => uploadFiles(HAPPYCO_THREAD_DRAFT_ID, files),
    [uploadFiles],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFileListUpload,
    noClick: true,
  });

  return (
    <DrawerContent {...getRootProps()} w={rightWidth} maxW={rightWidth}>
      <DropFilesHere isDragActive={isDragActive} width={rightWidth} />
      <DrawerHeader px={3} py={0}>
        <ComposeMessageThreadHeader
          draftId={HAPPYCO_THREAD_DRAFT_ID}
          title="New Thread"
          type={MessageThreadType.Thread}
          quillEditorRef={quillEditorRef}
        />
        <HappycoComposeHeader draftId={HAPPYCO_THREAD_DRAFT_ID} />
      </DrawerHeader>

      <DrawerBody w="full" />

      <DrawerFooter p={0} alignItems="flex-end">
        <QuillMessageComposer
          draftId={HAPPYCO_THREAD_DRAFT_ID}
          type={MessageThreadType.Thread}
          expandOnInit
          ref={quillEditorRef}
        />
      </DrawerFooter>
    </DrawerContent>
  );
};

export default memo(ComposeHappycoThreadDrawer);
